import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DropAnimation,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  closestCenter,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import React, { useState } from 'react'
import { KeyedMutator } from 'swr'
import { changeCourseBundleItemPosition } from 'modules/courses/bundles/course-bundle/api/course-bundle-api'
import {
  CourseBundleInterface,
  CourseBundleItemInterface,
} from 'modules/courses/bundles/course-bundle/types/course-bundle-interface'
import Course from 'modules/courses/bundles/course-bundle/components/course'
import CourseOverlay from 'modules/courses/bundles/course-bundle/components/course-overlay'

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4',
      },
    },
  }),
}

interface CourseBundleTableProps {
  items: CourseBundleItemInterface[]
  mutate: KeyedMutator<CourseBundleInterface>
}

function CourseBundleTable({ items, mutate }: CourseBundleTableProps) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const onDragCancel = async () => {
    setActiveId(null)
  }

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over?.id) {
      const activeIndex = items.findIndex(({ id }) => id === active.id)
      const overIndex = items.findIndex(({ id }) => id === over.id)
      const activeItem = items.find(({ id }) => id === active.id)
      const overItem = items.find(({ id }) => id === over.id)

      if (activeItem && overItem) {
        const clonedItems = [...items]
        await mutate(data => {
          if (data) {
            return {
              ...data,
              items: arrayMove(items, activeIndex, overIndex),
            }
          }
        }, false)
        try {
          await changeCourseBundleItemPosition(Number(active.id), {
            position: overIndex + 1,
          })
        } catch (error) {
          await mutate(data => {
            if (data) {
              return {
                ...data,
                items: clonedItems,
              }
            }
          }, false)
        }
      }
    }
    setActiveId(null)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={({ active }) => {
        setActiveId(active.id)
      }}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="mt-5 flex flex-col">
          {items.map(item => (
            <Course key={item.id} item={item} mutate={mutate} />
          ))}
        </div>
      </SortableContext>
      {activeId && (
        <DragOverlay dropAnimation={dropAnimationConfig}>
          <CourseOverlay item={items.find(item => item.id === activeId)} mutate={mutate} />
        </DragOverlay>
      )}
    </DndContext>
  )
}

export default CourseBundleTable
